/**
 * Indian state reference data for the registration form.
 *
 * The GST state code is the authoritative key: it is the first two digits of
 * every GSTIN and it decides place of supply, so the form collects it from a
 * list rather than as free text where a typo silently produces wrong tax.
 *
 * The city list is a convenience, not an authority - it covers the places a
 * hotel is most likely to be, and every state also accepts a city typed by
 * hand, because a guest house in a village is a real customer.
 */
export const INDIAN_STATES = [
    {
        code: '01',
        name: 'Jammu and Kashmir',
        cities: [
            'Srinagar',
            'Jammu',
            'Anantnag',
            'Baramulla',
            'Gulmarg',
            'Katra',
            'Pahalgam',
            'Sonamarg',
        ],
    },
    {
        code: '02',
        name: 'Himachal Pradesh',
        cities: [
            'Shimla',
            'Manali',
            'Dharamshala',
            'Kullu',
            'Dalhousie',
            'Kasauli',
            'Solan',
            'Mandi',
            'Palampur',
            'Spiti',
        ],
    },
    {
        code: '03',
        name: 'Punjab',
        cities: ['Amritsar', 'Ludhiana', 'Jalandhar', 'Patiala', 'Mohali', 'Bathinda', 'Pathankot'],
    },
    { code: '04', name: 'Chandigarh', cities: ['Chandigarh'] },
    {
        code: '05',
        name: 'Uttarakhand',
        cities: [
            'Dehradun',
            'Rishikesh',
            'Haridwar',
            'Nainital',
            'Mussoorie',
            'Almora',
            'Jim Corbett',
            'Auli',
            'Haldwani',
        ],
    },
    {
        code: '06',
        name: 'Haryana',
        cities: ['Gurugram', 'Faridabad', 'Panipat', 'Karnal', 'Ambala', 'Hisar', 'Kurukshetra'],
    },
    {
        code: '07',
        name: 'Delhi',
        cities: ['New Delhi', 'Delhi', 'Dwarka', 'Rohini', 'Saket', 'Aerocity'],
    },
    {
        code: '08',
        name: 'Rajasthan',
        cities: [
            'Jaipur',
            'Udaipur',
            'Jodhpur',
            'Jaisalmer',
            'Pushkar',
            'Ajmer',
            'Bikaner',
            'Mount Abu',
            'Ranthambore',
            'Kota',
        ],
    },
    {
        code: '09',
        name: 'Uttar Pradesh',
        cities: [
            'Lucknow',
            'Agra',
            'Varanasi',
            'Noida',
            'Ghaziabad',
            'Kanpur',
            'Prayagraj',
            'Mathura',
            'Ayodhya',
            'Meerut',
        ],
    },
    {
        code: '10',
        name: 'Bihar',
        cities: ['Patna', 'Gaya', 'Bodh Gaya', 'Muzaffarpur', 'Bhagalpur', 'Darbhanga'],
    },
    { code: '11', name: 'Sikkim', cities: ['Gangtok', 'Pelling', 'Namchi', 'Lachung', 'Ravangla'] },
    {
        code: '12',
        name: 'Arunachal Pradesh',
        cities: ['Itanagar', 'Tawang', 'Ziro', 'Bomdila', 'Pasighat'],
    },
    { code: '13', name: 'Nagaland', cities: ['Kohima', 'Dimapur', 'Mokokchung'] },
    { code: '14', name: 'Manipur', cities: ['Imphal', 'Churachandpur', 'Bishnupur'] },
    { code: '15', name: 'Mizoram', cities: ['Aizawl', 'Lunglei', 'Champhai'] },
    { code: '16', name: 'Tripura', cities: ['Agartala', 'Udaipur', 'Dharmanagar'] },
    { code: '17', name: 'Meghalaya', cities: ['Shillong', 'Cherrapunji', 'Tura', 'Dawki'] },
    {
        code: '18',
        name: 'Assam',
        cities: ['Guwahati', 'Jorhat', 'Dibrugarh', 'Silchar', 'Kaziranga', 'Tezpur'],
    },
    {
        code: '19',
        name: 'West Bengal',
        cities: [
            'Kolkata',
            'Darjeeling',
            'Siliguri',
            'Digha',
            'Kalimpong',
            'Durgapur',
            'Asansol',
            'Howrah',
        ],
    },
    {
        code: '20',
        name: 'Jharkhand',
        cities: ['Ranchi', 'Jamshedpur', 'Dhanbad', 'Bokaro', 'Deoghar'],
    },
    {
        code: '21',
        name: 'Odisha',
        cities: ['Bhubaneswar', 'Puri', 'Cuttack', 'Konark', 'Rourkela', 'Gopalpur'],
    },
    { code: '22', name: 'Chhattisgarh', cities: ['Raipur', 'Bilaspur', 'Bhilai', 'Jagdalpur'] },
    {
        code: '23',
        name: 'Madhya Pradesh',
        cities: [
            'Bhopal',
            'Indore',
            'Gwalior',
            'Jabalpur',
            'Khajuraho',
            'Ujjain',
            'Pachmarhi',
            'Orchha',
        ],
    },
    {
        code: '24',
        name: 'Gujarat',
        cities: [
            'Ahmedabad',
            'Surat',
            'Vadodara',
            'Rajkot',
            'Gandhinagar',
            'Bhuj',
            'Dwarka',
            'Somnath',
            'Statue of Unity',
        ],
    },
    {
        code: '26',
        name: 'Dadra and Nagar Haveli and Daman and Diu',
        cities: ['Silvassa', 'Daman', 'Diu'],
    },
    {
        code: '27',
        name: 'Maharashtra',
        cities: [
            'Mumbai',
            'Pune',
            'Nagpur',
            'Nashik',
            'Aurangabad',
            'Lonavala',
            'Mahabaleshwar',
            'Shirdi',
            'Thane',
            'Kolhapur',
            'Alibaug',
        ],
    },
    {
        code: '29',
        name: 'Karnataka',
        cities: [
            'Bengaluru',
            'Mysuru',
            'Mangaluru',
            'Hubballi',
            'Coorg',
            'Hampi',
            'Udupi',
            'Belagavi',
            'Chikmagalur',
            'Gokarna',
        ],
    },
    {
        code: '30',
        name: 'Goa',
        cities: [
            'Panaji',
            'Calangute',
            'Candolim',
            'Baga',
            'Anjuna',
            'Margao',
            'Vasco da Gama',
            'Palolem',
        ],
    },
    { code: '31', name: 'Lakshadweep', cities: ['Kavaratti', 'Agatti', 'Bangaram'] },
    {
        code: '32',
        name: 'Kerala',
        cities: [
            'Kochi',
            'Thiruvananthapuram',
            'Munnar',
            'Alappuzha',
            'Kovalam',
            'Thekkady',
            'Wayanad',
            'Kozhikode',
            'Thrissur',
            'Varkala',
        ],
    },
    {
        code: '33',
        name: 'Tamil Nadu',
        cities: [
            'Chennai',
            'Coimbatore',
            'Madurai',
            'Ooty',
            'Kodaikanal',
            'Thanjavur',
            'Mahabalipuram',
            'Rameswaram',
            'Tiruchirappalli',
            'Kanyakumari',
        ],
    },
    {
        code: '34',
        name: 'Puducherry',
        cities: ['Puducherry', 'Auroville', 'Karaikal', 'Yanam', 'Mahe'],
    },
    {
        code: '35',
        name: 'Andaman and Nicobar Islands',
        cities: ['Port Blair', 'Havelock Island', 'Neil Island'],
    },
    {
        code: '36',
        name: 'Telangana',
        cities: [
            'Hyderabad',
            'Secunderabad',
            'Warangal',
            'Karimnagar',
            'Nizamabad',
            'Ramoji Film City',
        ],
    },
    {
        code: '37',
        name: 'Andhra Pradesh',
        cities: [
            'Visakhapatnam',
            'Vijayawada',
            'Tirupati',
            'Guntur',
            'Nellore',
            'Araku Valley',
            'Amaravati',
        ],
    },
    { code: '38', name: 'Ladakh', cities: ['Leh', 'Kargil', 'Nubra', 'Pangong'] },
    { code: '97', name: 'Other Territory', cities: [] },
];
/** State codes, for validating a GSTIN prefix or a submitted state. */
export const STATE_CODES = INDIAN_STATES.map((s) => s.code);
export const stateByCode = (code) => INDIAN_STATES.find((s) => s.code === code);
export const stateName = (code) => stateByCode(code)?.name ?? 'Unknown state';
export const isValidStateCode = (code) => Boolean(code) && STATE_CODES.includes(code);
/** Suggested cities for a state; empty when we have no list for it. */
export const citiesForState = (code) => stateByCode(code)?.cities ?? [];
/** The state a GSTIN belongs to, from its first two digits. */
export const stateFromGstin = (gstin) => gstin && gstin.length >= 2 ? stateByCode(gstin.slice(0, 2)) : undefined;
/** States in the order the dropdown should show them. */
export const statesForSelect = () => [...INDIAN_STATES].sort((a, b) => a.name.localeCompare(b.name));
//# sourceMappingURL=india.js.map